import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { FeedbackFilterOption, FeedbackFilterParams, FeedbackListRequestParams } from '@components/feedbackFilter';
import { fetchProductOptions, parseProductCategories, parseProductList, type FeedbackProductCategory, type FeedbackProductItem, type FetchProductsParams, findProductByValue } from '@components/feedbackProductPicker';
import { parseProblemCategories, type FeedbackProblemCategory, type FeedbackProblemItem, findProblemByValue } from '@components/feedbackProblemPicker';


interface FeedbackListProps extends BasePageProps, RouteComponentProps {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}

interface FeedbackListState {
    loading: boolean;
    error: any
    pageLoaded: any
    feedbackList: any
    filterParams: FeedbackFilterParams
    searchInputValue: string
    solveStateList: FeedbackFilterOption[]
    /** 已选问题类型，用于筛选栏 Tag 展示（仅 1 项） */
    problemList: FeedbackFilterOption[]
    /** 问题类型弹框数据：/api/v3/feedbacks/problems */
    problemCategories: FeedbackProblemCategory[]
    /** 已选机型，用于筛选栏 Tag / 下拉文案展示（仅 1 项） */
    productList: FeedbackFilterOption[]
    /** 机型分类数据：/api/v5/products/categories */
    productCategories: FeedbackProductCategory[]
    daysList: FeedbackFilterOption[]
}

const DEFAULT_FILTER_PARAMS: FeedbackFilterParams = {
    searchValue: '',
    solveState: '',
    problemId: '',
    productId: '',
    daysValue: '',
    mine: '',
};

const DEFAULT_SOLVE_STATE_LIST: FeedbackFilterOption[] = [
    { name: '全部', id: -1, value: '' },
    { name: '已解决', id: 3, value: '3' },
    { name: '已回复', id: 2, value: '2' },
    { name: '待处理', id: 1, value: '1' },
    { name: '未解决', id: 0, value: '0' },
];

const DEFAULT_DAYS_LIST: FeedbackFilterOption[] = [
    { name: '全部', id: 0, value: '' },
    { name: '近3天', id: 3, value: '3' },
    { name: '近7天', id: 7, value: '7' },
    { name: '近15天', id: 15, value: '15' },
    { name: '近30天', id: 30, value: '30' },
];

@inject('app', 'thread')
@observer
class FeedbackList extends BasePage<FeedbackListProps, FeedbackListState> {
    namespace='feedbackList';
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        feedbackList: [],
        filterParams: { ...DEFAULT_FILTER_PARAMS },
        searchInputValue: '',
        solveStateList: DEFAULT_SOLVE_STATE_LIST,
        problemList: [],
        problemCategories: [],
        productList: [],
        productCategories: [],
        daysList: DEFAULT_DAYS_LIST,
    };

    componentDidMount() {
        this.fetchFilterOptions();
        super.componentDidMount();
    }

    /** 拉取问题类型分类、机型分类等筛选项，同时处理路由 problemId / productId 初始筛选 */
    fetchFilterOptions = async () => {
        const { thread } = this.props;
        if (!thread) return;

        try {
            const [problemsRes, productsRes] = await Promise.all([
                thread.getFeedbackProblems(),
                thread.getFeedbackProducts(),
            ]);
            const productCategories = parseProductCategories(productsRes);
            const problemCategories = parseProblemCategories(problemsRes);

            // 从路由读取 problemId / productId，若存在则回显到筛选栏并加入请求参数
            const params = new URLSearchParams(this.props.location.search);
            const routeProblemId = params.get('problemId');
            const routeProductId = params.get('productId');
            let problemId = '';
            let productId = '';
            let problemList: FeedbackFilterOption[] = [];
            let productList: FeedbackFilterOption[] = [];
            let solveState = DEFAULT_FILTER_PARAMS.solveState;

            if (routeProblemId) {
                const problem = findProblemByValue(problemCategories, routeProblemId);
                if (problem) {
                    problemId = problem.value;
                    problemList = [{
                        id: problem.id,
                        name: problem.name,
                        value: problem.value,
                        feedbackCount: problem.feedbackCount,
                        solvedCount: problem.solvedCount,
                    }];
                    // 路由自带 problemId 时默认筛选已解决状态
                    solveState = '3';
                }
            }

            if (routeProductId) {
                const product = await this.resolveRouteProduct(productCategories, routeProductId);
                if (product) {
                    productId = product.value;
                    productList = [{
                        id: product.id,
                        name: product.name,
                        value: product.value,
                    }];
                    // 路由仅带 productId 时同样默认筛选已解决状态
                    if (!problemId) {
                        solveState = '3';
                    }
                }
            }

            this.setState({
                problemCategories,
                productCategories,
                filterParams: {
                    ...DEFAULT_FILTER_PARAMS,
                    problemId,
                    productId,
                    solveState,
                },
                problemList,
                productList,
            }, () => {
                this.getFeedbackList();
            });
        } catch (error) {
            console.error('获取反馈筛选项失败:', error);
        }
    }

    /** 根据路由 productId 在各分类下查找对应机型 */
    resolveRouteProduct = async (
        productCategories: FeedbackProductCategory[],
        productId: string,
    ): Promise<FeedbackProductItem | null> => {
        const { thread } = this.props;
        if (!thread || !productCategories.length) return null;

        const productLists = await Promise.all(
            productCategories.map((category) => thread.getProducts({
                'page[number]': 1,
                'page[size]': 100,
                'filter[categoryId]': Number(category.id),
            }).then(parseProductList)),
        );

        for (const products of productLists) {
            const product = findProductByValue(products, productId);
            if (product) return product;
        }

        return null;
    }

    /** 构建 GET /api/v5/feedbacks 请求参数 */
    buildFeedbackListParams = (page = 1): FeedbackListRequestParams => {
        const { filterParams } = this.state;
        const params: FeedbackListRequestParams = {
            'page[number]': page,
            'page[size]': 20,
        };

        const keyword = filterParams.searchValue?.trim();
        if (keyword) {
            params['filter[keywords]'] = keyword;
        }
        if (filterParams.mine) {
            params['filter[mine]'] = filterParams.mine;
        }
        if (filterParams.solveState !== '') {
            params['filter[solveState]'] = Number(filterParams.solveState);
        }
        if (filterParams.problemId) {
            params['filter[problemId]'] = Number(filterParams.problemId);
        }
        if (filterParams.productId) {
            params['filter[productId]'] = Number(filterParams.productId);
        }
        if (filterParams.daysValue) {
            params['filter[days]'] = filterParams.daysValue;
        }

        return params;
    }

    getFeedbackList = () => {
        const { thread } = this.props;
        if (!thread) return;

        thread.listStore.clearList({ namespace: 'feedbackList' });
        window.scrollTo(0, 0);
        const params = this.buildFeedbackListParams(1);
        thread.getFeedbackList(params);
    }

    handleSearchChange = (value: string) => {
        this.setState({ searchInputValue: value });
    }

    handleSearchConfirm = () => {
        const { searchInputValue } = this.state;
        this.setState({
            filterParams: {
                ...this.state.filterParams,
                searchValue: searchInputValue.trim(),
            },
        }, () => {
            this.getFeedbackList();
        });
    }

    handleFilterChange = (key: keyof FeedbackFilterParams, value: string) => {
        this.setState({
            filterParams: {
                ...this.state.filterParams,
                [key]: value,
            },
            productList: key === 'productId' && !value ? [] : this.state.productList,
            problemList: key === 'problemId' && !value ? [] : this.state.problemList,
        }, () => {
            this.getFeedbackList();
        });
    }

    /** 拉取机型列表：按分类浏览，或按关键词全局搜索 */
    fetchProducts = async ({ categoryId, keywords }: FetchProductsParams) => {
        const { thread } = this.props;
        if (!thread) {
            return [];
        }

        if (!categoryId && !keywords?.trim()) {
            return [];
        }

        return fetchProductOptions(params => thread.getProducts(params), { categoryId, keywords });
    }

    /** 问题类型弹框确认：写入 filter[problemId]、清除路由 problemId 并刷新列表 */
    handleProblemSelect = (problem: FeedbackProblemItem | null) => {
        // 清除路由上的 problemId，避免后续操作重复触发初始筛选
        const searchParams = new URLSearchParams(this.props.location.search);
        if (searchParams.has('problemId')) {
            searchParams.delete('problemId');
            const newSearch = searchParams.toString();
            this.props.history.replace({
                pathname: this.props.location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            });
        }

        this.setState({
            filterParams: {
                ...this.state.filterParams,
                problemId: problem?.value || '',
                // 切换问题类型时恢复为所有状态
                solveState: '',
            },
            problemList: problem ? [{
                id: problem.id,
                name: problem.name,
                value: problem.value,
                feedbackCount: problem.feedbackCount,
                solvedCount: problem.solvedCount,
            }] : [],
        }, () => {
            this.getFeedbackList();
        });
    }

    /** 机型弹框确认：写入 filter[productId]、清除路由 productId 并刷新列表 */
    handleProductSelect = (product: FeedbackProductItem | null) => {
        // 清除路由上的 productId，避免后续操作重复触发初始筛选
        const searchParams = new URLSearchParams(this.props.location.search);
        if (searchParams.has('productId')) {
            searchParams.delete('productId');
            const newSearch = searchParams.toString();
            this.props.history.replace({
                pathname: this.props.location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            });
        }

        this.setState({
            filterParams: {
                ...this.state.filterParams,
                productId: product?.value || '',
            },
            productList: product ? [{
                id: product.id,
                name: product.name,
                value: product.value,
            }] : [],
        }, () => {
            this.getFeedbackList();
        });
    }

    /** H5「筛选」弹窗确认：批量更新状态与时间后刷新列表 */
    handleAdvancedFilterConfirm = (values: Pick<FeedbackFilterParams, 'solveState' | 'daysValue'>) => {
        this.setState({
            filterParams: {
                ...this.state.filterParams,
                ...values,
            },
        }, () => {
            this.getFeedbackList();
        });
    }

    handleLoadMore = () => {
        const { thread } = this.props;
        if (!thread) return;

        const list = thread.listStore.lists['feedbackList'];
        if (!list) return;
        if (!list.hasMore || list.loading) return;

        const currentPage = list.attribs?.page || 1;
        const nextPage = currentPage + 1;
        const params = this.buildFeedbackListParams(nextPage);
        thread.getFeedbackList(params);
    }

    render() {
        const { app, thread } = this.props;
        const platform = app?.platform || 'h5';
        const namespace = this.namespace;
        const listStore = thread?.listStore;
        const list = listStore?.lists[namespace];
        const feedbackList = listStore?.getList({ namespace }) || [];
        const loading = list?.loading || false;
        const hasMore = list?.hasMore ?? true;
        const {
            filterParams,
            searchInputValue,
            solveStateList,
            problemList,
            problemCategories,
            productList,
            productCategories,
            daysList,
        } = this.state;
        const selectedProblem = filterParams.problemId
            ? findProblemByValue(problemCategories, filterParams.problemId)
            : null;
        const totalCount = selectedProblem?.feedbackCount ?? 0;

        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        }

        const layoutProps = {
            ...this.props,
            feedbackList,
            loading,
            hasMore,
            onLoadMore: this.handleLoadMore,
            filterParams,
            searchInputValue,
            solveStateList,
            problemList,
            problemCategories,
            productList,
            productCategories,
            daysList,
            onSearchChange: this.handleSearchChange,
            onSearchConfirm: this.handleSearchConfirm,
            onFilterChange: this.handleFilterChange,
            onAdvancedFilterConfirm: this.handleAdvancedFilterConfirm,
            onProblemSelect: this.handleProblemSelect,
            onProductSelect: this.handleProductSelect,
            fetchProducts: this.fetchProducts,
            searchKeyword: filterParams.searchValue,
            totalCount,
        };

        return (
            <div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (
                    <PC {...layoutProps} />
                ) : (
                    <H5 {...layoutProps} />
                )}
            </div>
        );
    }
}

export default withRouter(FeedbackList);
